import type { TuituiTeamsTarget } from "@qihoo/tuitui-bot-sdk";
import { createTuituiClient } from "./sdk";

// ChatType定义与SessionKey定义一致，不可随意修改
// https://docs.openclaw.ai/channels/channel-routing#session-key-shapes-examples
export const CHAT_TYPE_DIRECT = 'direct' as const;
export const CHAT_TYPE_GROUP  = 'group'  as const;
export const CHAT_TYPE_CHANNEL  = 'channel'  as const;
export type ChatType = typeof CHAT_TYPE_DIRECT | typeof CHAT_TYPE_GROUP | typeof CHAT_TYPE_CHANNEL;

export function guessChatType(chatId: string): ChatType {
  if (chatId.startsWith("teams_")) return CHAT_TYPE_CHANNEL;
  const isMobileNumber = /^1[3-9]\d{9}$/.test(chatId);
  if (isMobileNumber) return CHAT_TYPE_DIRECT; // fix: 部分租户号为手机号被误猜测为群id的问题。大陆手机号格式
  if (/^\d+$/.test(chatId)) return CHAT_TYPE_GROUP;
  return CHAT_TYPE_DIRECT;
}

export async function guessChatTypeV2(
  account: any,
  chatId: string,
): Promise<{ chatId: string; chatType: ChatType }> {
  const normalizedChatId = String(chatId || '').trim();
  const chatType = guessChatType(normalizedChatId);

  if (chatType === CHAT_TYPE_GROUP) {
    try {
      const channelInfo = await createTuituiClient(account).teams.getChannelInfo(normalizedChatId);
      return {
        chatId: teamsBuildChatId(channelInfo.team_id, normalizedChatId, ''),
        chatType: CHAT_TYPE_CHANNEL,
      };
    } catch {
      // 无法查询到频道信息时，保留原始推断结果。
    }
  }

  return { chatId: normalizedChatId, chatType };
}


export function teamsBuildChatId(team_id: string, channel_id:string, thread_id:string) : string{
  let ret = `teams_${team_id}_${channel_id}`;
  if(thread_id) ret += `_${thread_id}`;
  return ret;
}

export function teamsParseChatId(chatId: string): TuituiTeamsTarget {
  const [team_id, channel_id, parent_id] = chatId.replace(/^teams_/, '').split('_');
  
  if (!team_id || !channel_id) {
    throw new Error('Invalid teams chat ID format');
  }

  const ret = { team_id, channel_id} as TuituiTeamsTarget;
  if(parent_id) ret.parent_id = parent_id;
  return ret;
}


export interface TuiTuiSessionKeyInfo {
  chat_type: ChatType,
  channel_id: string;
  thread_id: string;
}

// 暂时只支持频道(后续可以完善)
// 格式 agent:coder:tuitui:channel:123:thread:456
export function parseSessionKey(str: string): TuiTuiSessionKeyInfo {
  const info: TuiTuiSessionKeyInfo = { chat_type: CHAT_TYPE_DIRECT, channel_id: '', thread_id: '' };

  if (!str.includes('tuitui:channel:')) {
    return info;
  }

  info.chat_type = CHAT_TYPE_CHANNEL;

  const parts = str.split(':');
  const channelIndex = parts.findIndex(part => part === 'channel');
  if (channelIndex !== -1 && parts[channelIndex + 1]) {
    info.channel_id = parts[channelIndex + 1];
  }

  const threadIndex = parts.findIndex(part => part === 'thread');
  if (threadIndex !== -1 && parts[threadIndex + 1]) {
    info.thread_id = parts[threadIndex + 1];
  }

  return info;
}

export function parseChatFromSessionKey(sessionKey: string): { chatType: ChatType; chatId: string } | null {
  if (!sessionKey) return null;

  const parts = sessionKey.split(':');
  const tuituiIndex = parts.indexOf('tuitui');
  if (tuituiIndex === -1 || tuituiIndex + 2 > parts.length - 1) return null;

  const chatType = parts[tuituiIndex + 1] as ChatType;
  const chatId = parts.slice(tuituiIndex + 2).join(':');

  if (!chatId) return null;
  if (chatType !== CHAT_TYPE_DIRECT && chatType !== CHAT_TYPE_GROUP) return null;

  return { chatType, chatId };
}

/**
 * 将推推内部的 chatId 转换为 OpenClaw 路由层使用的 peer.id 格式
 * 对于单聊和群聊，直接保持原样；对于频道（Teams），转换为 `channel_id:thread:parent_id` 格式
 */
export function buildPeerIdFromChatId(chatId: string, chatType: ChatType): string {
  if (chatType === CHAT_TYPE_CHANNEL) {
    const { channel_id, parent_id } = teamsParseChatId(chatId);
    let id = `${channel_id}`;
    if (parent_id) {
      id += `:thread:${parent_id}`;
    }
    return id;
  }
  return chatId;
}
